import { useCallback, useEffect, useRef, useState } from "react";

import type { AssistantChatControls } from "../../helpers/types";

export type AssistantWidgetCloseTrigger = "user" | "navigation";

interface UseAssistantWidgetProps {
  onOpen?: () => void;
  onClose?: (params: { messageCount: number; trigger: AssistantWidgetCloseTrigger }) => void;
  onConversationRestarted?: () => void;
}

export const useAssistantWidget = <TControls extends AssistantChatControls = AssistantChatControls>({
  onOpen,
  onClose,
  onConversationRestarted,
}: UseAssistantWidgetProps = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [controls, setControls] = useState<TControls | null>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const messageCount = controls?.messageCount ?? 0;

  const open = useCallback(() => {
    setIsOpen(true);
    setHasOpened(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(
    (trigger: AssistantWidgetCloseTrigger = "user") => {
      setIsOpen(false);
      onClose?.({ messageCount, trigger });
    },
    [messageCount, onClose]
  );

  const requestClose = () => {
    const closeByUser = () => close("user");
    if (controls) {
      controls.requestExit(closeByUser);
    } else {
      closeByUser();
    }
  };

  const startNew = () => {
    const restart = () => {
      controls?.startNew();
      onConversationRestarted?.();
    };
    if (controls) {
      controls.requestExit(restart);
    } else {
      restart();
    }
  };

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      launcherRef.current?.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  return { isOpen, hasOpened, open, close, requestClose, startNew, controls, setControls, launcherRef };
};
